import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, FrownIcon, LaughIcon, SmileIcon } from 'lucide-react';
export type ServiceRating = 'insatisfait' | 'satisfait' | 'tres-satisfait';
const ease = [0.23, 1, 0.32, 1] as const;
export const serviceRatings: {
  id: ServiceRating;
  label: string;
  Icon: typeof SmileIcon;
}[] = [{
  id: 'insatisfait',
  label: 'Pas du tout',
  Icon: FrownIcon
}, {
  id: 'satisfait',
  label: 'Oui',
  Icon: SmileIcon
}, {
  id: 'tres-satisfait',
  label: 'Parfaitement',
  Icon: LaughIcon
}];
interface ServiceSatisfactionProps {
  value: ServiceRating | null;
  onChange: (rating: ServiceRating) => void;
  /** « compact » : discret, sous le numéro de requête ; « message » : dans le fil de la Ville. */
  variant: 'compact' | 'message';
}

/** Satisfaction sur la qualité du service rendu pour une requête résolue (trois smileys). */
export function ServiceSatisfaction({
  value,
  onChange,
  variant
}: ServiceSatisfactionProps) {
  const chosen = serviceRatings.find(item => item.id === value);
  const compact = variant === 'compact';
  const titleId = `satisfaction-${variant}`;
  if (chosen) {
    return <motion.p initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.2,
      ease
    }} role="status" className={`flex items-center gap-2 text-ville-navy ${compact ? 'text-xs' : 'mt-3 text-sm'}`} data-id="element-1443" data-mp="10s74y3k6aif7">
        <chosen.Icon className={`shrink-0 text-ville-indigo ${compact ? 'h-5 w-5' : 'h-7 w-7'}`} strokeWidth={1.6} aria-label={chosen.label} data-id="element-1444" data-mp="1nq0o8w1ghdma" />
        <span className="inline-flex items-center gap-1 font-bold" data-id="element-1445" data-mp="1xhev5ovqlfpg">
          <CheckIcon className="h-3.5 w-3.5 text-ville-indigo" aria-hidden="true" data-id="element-1446" data-mp="1xipzbpt3gph3" />
          Merci pour votre avis !
        </span>
      </motion.p>;
  }
  return <div className={compact ? 'flex items-center gap-2' : 'mt-3 rounded-2xl bg-ville-mist/50 px-4 py-3'} data-id="element-1447" data-mp="27fkwa49pyht9">
      <p id={titleId} className={compact ? 'text-xs font-semibold text-slate-500' : 'text-sm font-bold text-ville-navy'} data-id="element-1448" data-mp="22ht1n2howhld">
        {compact ? 'Qualité du service' : 'Êtes-vous satisfaite du service ?'}
      </p>
      <div role="group" aria-labelledby={titleId} className={compact ? 'flex items-center gap-1' : 'mt-2 flex items-start justify-center gap-6'} data-id="element-1449" data-mp="2eg6fycvvmy83">
        {serviceRatings.map(({
        id,
        label,
        Icon
      }) => <button key={id} type="button" onClick={() => onChange(id)} aria-label={compact ? label : undefined} title={compact ? label : undefined} className={`group flex flex-col items-center text-slate-400 transition-colors duration-150 ease-ville hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${compact ? 'rounded-full p-1' : 'w-20 gap-1 rounded-2xl py-1 text-slate-500'}`} data-id="element-1450" data-mp="2t7owqcwk8wsi">
            <Icon className={`transition-transform duration-150 ease-ville group-hover:scale-110 group-active:scale-95 ${compact ? 'h-5 w-5' : 'h-8 w-8'}`} strokeWidth={1.6} aria-hidden="true" data-id="element-1451" data-mp="1ctfhc8aaqidt" />
            {!compact && <span className="text-xs font-semibold" data-id="element-1452" data-mp="vkcv7apwvyix">{label}</span>}
          </button>)}
      </div>
    </div>;
}